import { ROUND_NAMES, hhmm } from '@jobfair/shared';
import type { LiveState, Phase } from '../types';
import { Icon } from './Icon';

const ACCENTS = ['var(--c3)', 'var(--c0)', 'var(--c1)', 'var(--c2)'];

type Step = 'done' | 'current' | 'next' | 'up';

/** Index 0 is the central gathering; 1-3 are the rounds. */
export function stepState(ph: Phase, i: number): Step {
  const S = ph.state;
  if (S === 'before') return 'up';
  if (S === 'ended') return 'done';
  if (S === 'keynote') return i === 0 ? 'current' : 'up';
  if (i === 0) return 'done';
  const k = i - 1;
  if (ph.round >= 0) return k < ph.round ? 'done' : k === ph.round ? 'current' : 'up';
  if (ph.next >= 0) return k < ph.next ? 'done' : k === ph.next ? 'next' : 'up';
  return 'done';
}

export function Timeline({ st, ph }: { st: LiveState; ph: Phase }) {
  const items = [
    { label: 'התכנסות מרכזית', start: ph.cs, end: ph.ce, raw: [st.convStart, st.convEnd] },
    ...st.rounds.map((r, i) => ({
      label: ROUND_NAMES[i]!,
      start: ph.R[i]!.start,
      end: ph.R[i]!.end,
      raw: [r.start, r.end],
    })),
  ];
  const stateLabel = {
    done: (
      <>
        <Icon n="check" s={14} />
        הסתיים
      </>
    ),
    current: (
      <>
        <span className="live-dot" style={{ width: 7, height: 7 }} />
        מתקיים כעת
      </>
    ),
    next: 'הבא בתור',
    up: '',
  } as const;

  return (
    <section className="tl-wrap wrap" aria-labelledby="tl-h">
      <h2 id="tl-h" className="sr-only">
        לוח הזמנים
      </h2>
      <ol className="tl">
        {items.map((it, i) => {
          const c = stepState(ph, i);
          const t = ph.valid
            ? `${hhmm(it.start)}–${hhmm(it.end)}`
            : `${it.raw[0] || '--'}–${it.raw[1] || '--'}`;
          return (
            <li
              key={i}
              className={c}
              style={{ '--ac': ACCENTS[i] } as React.CSSProperties}
              aria-current={c === 'current' ? 'step' : undefined}
            >
              <span className="t-num" aria-hidden="true">
                {c === 'done' ? (
                  <Icon n="check" s={15} />
                ) : i === 0 ? (
                  <Icon n="broadcast" s={15} />
                ) : (
                  i
                )}
              </span>
              <span className="t-body">
                <span className="t-time">{t}</span>
                <span className="t-label">{it.label}</span>
                {stateLabel[c] && <span className="t-state">{stateLabel[c]}</span>}
              </span>
            </li>
          );
        })}
      </ol>
      {st.delay > 0 && (
        <p className="tl-delay">פתיחת המפגשים נדחתה ב-{st.delay} דקות. השעות שלמעלה מעודכנות.</p>
      )}
    </section>
  );
}
